export type ImportActivityType = 'regular' | 'once-off' | 'flexible' | 'event' | 'self-directed';

export const ACTIVITY_TYPES: ImportActivityType[] = ['regular', 'once-off', 'flexible', 'event', 'self-directed'];

export const FREQUENCIES = [
  'daily',
  'weekly',
  'fortnightly',
  'weekdays',
  'weekends',
  'monthly-date',
  'monthly-nth-day',
  'yearly',
] as const;

export type ImportFrequency = typeof FREQUENCIES[number];

export const WEEKDAY_CODES = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

export const INDIVIDUAL_REPORT_OPTIONS = ['optional', 'mandatory'];

export const AUTO_REPORT_VALUE = 'auto';

export type ColumnKind =
  | 'text'
  | 'longtext'
  | 'activityType'
  | 'program'
  | 'frequency'
  | 'bool'
  | 'number'
  | 'date'
  | 'time'
  | 'reportType'
  | 'individualReports'
  | 'address';

export interface ColumnDef {
  key: string;
  header: string;
  group: string;
  kind: ColumnKind;
  width: number;
  appliesTo?: ImportActivityType[];
  help?: string;
  session?: number;
  oneOff?: number;
}

export const ACTIVITY_COLUMNS: ColumnDef[] = [
  { key: 'activityType', header: 'Type', group: 'Activity', kind: 'activityType', width: 130 },
  { key: 'program', header: 'Program', group: 'Activity', kind: 'program', width: 180 },
  { key: 'description', header: 'Description', group: 'Activity', kind: 'longtext', width: 240 },
  { key: 'tags', header: 'Tags', group: 'Activity', kind: 'text', width: 160, help: 'Tag names separated by ;' },
  {
    key: 'startDate',
    header: 'Start Date',
    group: 'Schedule',
    kind: 'date',
    width: 120,
    appliesTo: ['regular', 'once-off'],
  },
  { key: 'endDate', header: 'End Date', group: 'Schedule', kind: 'date', width: 120, appliesTo: ['regular'] },
  { key: 'frequency', header: 'Frequency', group: 'Schedule', kind: 'frequency', width: 150, appliesTo: ['regular'] },
  {
    key: 'repeatDays',
    header: 'Repeat Days',
    group: 'Schedule',
    kind: 'text',
    width: 120,
    appliesTo: ['regular'],
    help: 'Weekly/fortnightly only, e.g. MO;WE. Defaults to the start date weekday',
  },
  {
    key: 'repeatInterval',
    header: 'Interval',
    group: 'Schedule',
    kind: 'number',
    width: 90,
    appliesTo: ['regular'],
    help: 'Repeat every N days/weeks/months/years. Defaults to 1',
  },
  {
    key: 'dates',
    header: 'Dates',
    group: 'Schedule',
    kind: 'text',
    width: 220,
    appliesTo: ['event', 'flexible'],
    help: 'Dates separated by ;. A roster is created for each date',
  },
  { key: 'address', header: 'Address', group: 'Location', kind: 'address', width: 240 },
  { key: 'remoteDetails', header: 'Remote Details', group: 'Location', kind: 'text', width: 180 },
  { key: 'restricted', header: 'Restricted', group: 'Settings', kind: 'bool', width: 100 },
  { key: 'hidden', header: 'Hidden', group: 'Settings', kind: 'bool', width: 90 },
  { key: 'openRoster', header: 'Open Roster', group: 'Settings', kind: 'bool', width: 110 },
  { key: 'requireRosterApproval', header: 'Roster Approval', group: 'Settings', kind: 'bool', width: 130 },
  { key: 'maxTeam', header: 'Max Team', group: 'Settings', kind: 'number', width: 100 },
  { key: 'priority', header: 'Priority', group: 'Settings', kind: 'bool', width: 90 },
  { key: 'allowCico', header: 'Allow CICO', group: 'Settings', kind: 'bool', width: 100 },
  { key: 'allowIndividualShiftTimes', header: 'Individual Shift Times', group: 'Settings', kind: 'bool', width: 160 },
  { key: 'allowRosterComments', header: 'Roster Comments', group: 'Settings', kind: 'bool', width: 140 },
  {
    key: 'addressRevealBeforeDays',
    header: 'Reveal Address (days before)',
    group: 'Settings',
    kind: 'number',
    width: 190,
  },
  {
    key: 'addressHideAfterDays',
    header: 'Hide Address (days after)',
    group: 'Settings',
    kind: 'number',
    width: 180,
  },
  { key: 'addressHidingExempt', header: 'Address Hiding Exempt', group: 'Settings', kind: 'bool', width: 170 },
  {
    key: 'requiredRoles',
    header: 'Required Roles',
    group: 'Settings',
    kind: 'text',
    width: 160,
    help: 'Role names separated by ;',
  },
];

export const NAME_COLUMN: ColumnDef = {
  key: 'activityName',
  header: 'Activity Name',
  group: 'Activity',
  kind: 'text',
  width: 200,
};

interface SessionFieldDef {
  suffix: string;
  header: string;
  kind: ColumnKind;
  width: number;
  help?: string;
}

export const SESSION_FIELDS: SessionFieldDef[] = [
  { suffix: '', header: 'Name', kind: 'text', width: 160 },
  { suffix: '_desc', header: 'Description', kind: 'longtext', width: 200 },
  { suffix: '_start', header: 'Start', kind: 'time', width: 90 },
  { suffix: '_end', header: 'End', kind: 'time', width: 90 },
  { suffix: '_min', header: 'Min', kind: 'number', width: 70 },
  { suffix: '_max', header: 'Max', kind: 'number', width: 70 },
  {
    suffix: '_report_type',
    header: 'Report Type',
    kind: 'reportType',
    width: 170,
    help: `Report type name. Blank = no report, "${AUTO_REPORT_VALUE}" = no report (impact tracked)`,
  },
  { suffix: '_individual_reports', header: 'Individual Reports', kind: 'individualReports', width: 150 },
  { suffix: '_required_roles', header: 'Required Roles', kind: 'text', width: 150, help: 'Role names separated by ;' },
];

export const sessionKey = (n: number, suffix: string) => `session_${n}${suffix}`;

export const getSessionColumns = (n: number): ColumnDef[] =>
  SESSION_FIELDS.map((field) => ({
    key: sessionKey(n, field.suffix),
    header: field.header,
    group: `Session ${n}`,
    session: n,
    kind: field.kind,
    width: field.width,
    help: field.help,
  }));

export const ONE_OFF_TYPES: ImportActivityType[] = ['regular', 'event'];

export const ONE_OFF_FIELDS: SessionFieldDef[] = [
  {
    suffix: '_date',
    header: 'Dates',
    kind: 'text',
    width: 180,
    help: 'Dates separated by ;. The session is added to each date',
  },
  ...SESSION_FIELDS.filter((field) => !['_individual_reports', '_required_roles'].includes(field.suffix)),
];

export const oneOffKey = (n: number, suffix: string) => `session_oneoff_${n}${suffix}`;

export const getOneOffColumns = (n: number): ColumnDef[] =>
  ONE_OFF_FIELDS.map((field) => ({
    key: oneOffKey(n, field.suffix),
    header: field.header,
    group: `One-off ${n}`,
    oneOff: n,
    kind: field.kind,
    width: field.width,
    help: field.help,
    appliesTo: ONE_OFF_TYPES,
  }));

const flatten = (groups: ColumnDef[][]) => groups.reduce((acc, columns) => acc.concat(columns), [] as ColumnDef[]);

export const getAllColumns = (sessionCount: number, oneOffCount = 0): ColumnDef[] => [
  NAME_COLUMN,
  ...ACTIVITY_COLUMNS,
  ...flatten(Array.from({ length: sessionCount }, (_unused, i) => getSessionColumns(i + 1))),
  ...flatten(Array.from({ length: oneOffCount }, (_unused, i) => getOneOffColumns(i + 1))),
];

export const isColumnApplicable = (column: ColumnDef, activityType: string) =>
  !column.appliesTo || column.appliesTo.includes(activityType as ImportActivityType);

export const getColumnOptions = (
  column: ColumnDef,
  programNames: string[],
  reportTypeNames: string[]
): string[] | null => {
  switch (column.kind) {
    case 'activityType':
      return ACTIVITY_TYPES;
    case 'frequency':
      return [...FREQUENCIES];
    case 'program':
      return programNames;
    case 'reportType':
      return [AUTO_REPORT_VALUE, ...reportTypeNames];
    case 'individualReports':
      return INDIVIDUAL_REPORT_OPTIONS;
    case 'bool':
      return ['yes', 'no'];
    default:
      return null;
  }
};
